import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "@vimaana/ui";

/**
 * Das eigene Mailpostfach (Backend: hetzner/webhook/kontor_mail.py).
 *
 * Jeder Inhaber verbindet sein eigenes Google-Konto. Belege kommen aus SEINEM
 * Postfach, Angebote und Rechnungen gehen unter SEINER Adresse raus.
 */

export type MailKonto = {
  verbunden: boolean;
  email: string;
  provider: string;
  verbunden_at: string | null;
  letzter_sync_at: string | null;
  letzter_sync_status: string;
  /** Die Adresse, die in der Google Cloud Console freigeschaltet sein muss. */
  weiterleitung: string;
  /** Gesetzt, wenn ein Konto hinterlegt, aber nicht mehr benutzbar ist. */
  hinweis?: string;
};

export type BelegSyncErgebnis = {
  geprueft: number;
  neu: number;
  /** Schon bekannt — gleicher Inhalts-Hash. */
  uebersprungen: number;
  /** Geprueft und als Nicht-Beleg verworfen (Angebot, Anfrage, Beiwerk). */
  kein_beleg: number;
  konto: string;
  /** Menschenlesbares Zeitfenster, das der Server tatsaechlich genommen hat. */
  zeitraum: string;
  hinweise: string[];
  /**
   * IDs der frisch angelegten Belege. Der Abruf selbst wertet nichts aus —
   * die Erkennung laeuft danach je Beleg einzeln ueber
   * POST /belege/{id}/extract, damit der Fortschritt sichtbar ist und ein
   * grosser Abruf nicht in einen Timeout laeuft.
   */
  neue_ids: string[];
};

const mailKey = ["kontor", "mail", "konto"] as const;

export function useMailKonto() {
  return useQuery<MailKonto>({
    queryKey: mailKey,
    queryFn: () => apiGet<MailKonto>("/api/kontor/mail/konto"),
    staleTime: 60_000,
  });
}

/**
 * Startet das Verbinden. Der Browser wird zu Google geschickt; von dort führt
 * die Weiterleitung zurück auf /einstellungen?mail=verbunden.
 */
export function useMailVerbinden() {
  return useMutation({
    mutationFn: () => apiPost<{ url: string }>("/api/kontor/mail/oauth/start", {}),
    onSuccess: (d) => {
      if (d?.url) window.location.href = d.url;
    },
  });
}

export function useMailTrennen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<{ getrennt: boolean }>("/api/kontor/mail/konto"),
    onSuccess: () => qc.invalidateQueries({ queryKey: mailKey }),
  });
}

/**
 * Belege aus dem eigenen Postfach holen — beliebig oft wiederholbar.
 * `tage = 0` heisst: ab dem letzten erfolgreichen Lauf (mit zwei Tagen
 * Ueberlappung, weil Mails verspaetet eintreffen).
 */
export function useBelegeAusMailHolen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tage: number) =>
      apiPost<BelegSyncErgebnis>("/api/kontor/mail/belege/sync", { tage }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mailKey });
      qc.invalidateQueries({ queryKey: ["belege"] });
      qc.invalidateQueries({ queryKey: ["finanzen"] });
    },
  });
}

/** Angebot oder Rechnung als PDF aus dem eigenen Postfach verschicken. */
export function useBelegVersenden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { invoiceId: string; an?: string; betreff?: string; text?: string; cc?: string }) =>
      apiPost<{ message_id: string; an: string; betreff: string }>(
        `/api/kontor/rechnungen/${v.invoiceId}/senden`,
        { an: v.an ?? "", betreff: v.betreff ?? "", text: v.text ?? "", kopie_an_mich: true, cc: v.cc ?? "" },
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["invoices"] }),
  });
}

/**
 * Das gespeicherte Anschreiben je Belegart (Backend: `mail_vorlage`).
 *
 * Der Server antwortet immer — hat der Inhaber nichts gemerkt, kommt die
 * Vorgabe mit `eigen: false`. Deshalb gibt es im Browser keinen zweiten
 * Vorgabetext, der auseinanderlaufen könnte.
 */
export type MailVorlageArt = "quote" | "invoice";

export type MailVorlage = {
  /** "" bei der eingebauten Vorgabe — die hat keine Zeile in der Datenbank. */
  id: string;
  kind: MailVorlageArt;
  name: string;
  betreff: string;
  text: string;
  ist_standard: boolean;
  /** true = vom Inhaber gemerkt, false = Vorgabe aus dem Code. */
  eigen: boolean;
};

export type MailVorlagen = {
  platzhalter: string[];
  /** Die Fassung, die gilt — Vorgabe des Inhabers oder eingebauter Text. */
  vorlagen: Record<MailVorlageArt, MailVorlage>;
  /** Alle benannten Vorlagen je Belegart (Migration 149), Vorgabe zuerst. */
  listen: Record<MailVorlageArt, MailVorlage[]>;
};

/** Antwort der Pflegewege: die neue Liste und die Fassung, die jetzt gilt. */
export type MailVorlagenStand = {
  listen: MailVorlage[];
  vorlage: MailVorlage;
};

const vorlagenKey = ["kontor", "mail", "vorlagen"] as const;

export function useMailVorlagen() {
  return useQuery<MailVorlagen>({
    queryKey: vorlagenKey,
    queryFn: () => apiGet<MailVorlagen>("/api/kontor/mail/vorlagen"),
    staleTime: 60_000,
  });
}

/** Den aktuellen Text als Vorlage übernehmen — ersetzt die bisherige. */
export function useMailVorlageSpeichern() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { kind: MailVorlageArt; betreff: string; text: string }) =>
      apiPut<MailVorlage>("/api/kontor/mail/vorlage", v),
    onSuccess: () => qc.invalidateQueries({ queryKey: vorlagenKey }),
  });
}

/** Eine benannte Vorlage entfernen. War sie die Vorgabe, rückt die nächste nach. */
export function useMailVorlageLoeschen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<MailVorlagenStand>(`/api/kontor/mail/vorlage/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: vorlagenKey }),
  });
}

/**
 * Eine benannte Vorlage anlegen oder ändern.
 *
 * POST statt PUT: die CORS-Allowlist des Webhooks kennt kein PUT — dieselbe
 * Regel wie in den Stammdaten. (`useMailVorlageSpeichern` oben ist der alte
 * Ein-Klick-Weg und bleibt auf PUT, weil diese Route es schon immer war.)
 */
export function useMailVorlagePflegen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: {
      id?: string; kind: MailVorlageArt; name: string;
      betreff: string; text: string; ist_standard?: boolean;
    }) => apiPost<MailVorlagenStand>("/api/kontor/mail/vorlage/pflege", v),
    onSuccess: () => qc.invalidateQueries({ queryKey: vorlagenKey }),
  });
}

/** Diese Vorlage wird die Vorgabe ihrer Belegart. */
export function useMailVorlageStandard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<MailVorlagenStand>(`/api/kontor/mail/vorlage/${id}/standard`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: vorlagenKey }),
  });
}
